import { Badge, Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Info, Lock, Server, ShieldAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { type BlocklistResponse, type GovernanceOverviewResponse, getGovernanceOverviewOptions } from "@/api";
import { EmptyState } from "@/components/build/EmptyState";
import { PageSection, StatRow } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";
import { AdminPage } from "../AdminPage";
import { DeploymentTable } from "./DeploymentTable";
import { PolicyItem } from "./PolicyItem";

const FOUNDRY_URL = "https://ai.azure.com";

function Muted({ text }: Readonly<{ text: string }>) {
  return (
    <Text variant="body-sm" tone="tertiary">
      {text}
    </Text>
  );
}

function ResourceLine({ overview }: Readonly<{ overview: GovernanceOverviewResponse }>) {
  const { t } = useTranslation("adminGovernance");
  const accounts = overview.accounts ?? [];
  return (
    <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-4">
      <Server size={14} className="hidden text-content-tertiary md:block" aria-label={t("resources")} />
      {accounts.map((acc) => (
        <Text key={acc.name} as="span" variant="caption" tone="secondary">
          {t("account", { label: acc.label || acc.name, name: acc.name })}
        </Text>
      ))}
      {accounts[0]?.resourceGroup && (
        <Text as="span" variant="caption" tone="tertiary">
          {t("resourceGroup", { name: accounts[0].resourceGroup })}
        </Text>
      )}
      <a
        href={FOUNDRY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-content-primary underline underline-offset-2 md:ml-auto"
      >
        <Text as="span" variant="caption">
          {t("openFoundry")}
        </Text>
        <ExternalLink size={12} aria-hidden />
      </a>
    </div>
  );
}

function Blocklists({ blocklists }: Readonly<{ blocklists: BlocklistResponse[] }>) {
  const { t } = useTranslation("adminGovernance");
  return (
    <ul className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
      {blocklists.map((bl) => (
        <li key={bl.name} className="flex items-center justify-between gap-4 py-3">
          <span className="flex min-w-0 items-start gap-2">
            <Lock size={14} className="mt-1 shrink-0 text-content-tertiary" aria-hidden />
            <span className="flex min-w-0 flex-col">
              <Text as="span" variant="body-sm">
                {bl.name}
              </Text>
              {bl.description && (
                <Text as="span" variant="caption" tone="tertiary">
                  {bl.description}
                </Text>
              )}
            </span>
          </span>
          <Badge variant="neutral">{t("blocklists.terms", { count: bl.itemCount ?? 0 })}</Badge>
        </li>
      ))}
    </ul>
  );
}

/** /admin/governance: read-only view of Azure AI content safety (policies, deployments, blocklists). */
export function GovernancePage() {
  const { t } = useTranslation("adminGovernance");
  const governance = useQuery(getGovernanceOverviewOptions());
  const overview = governance.data?.data;

  const body = () => {
    if (governance.isPending) return <Skeleton className="h-16 w-full" />;
    if (governance.isError) {
      return (
        <Text tone="error" role="alert">
          {t("loadFailed")}
        </Text>
      );
    }
    if (!overview?.configured) {
      return (
        <EmptyState
          icon={<ShieldAlert size={32} />}
          title={t("notConfigured")}
          body={overview?.message || t("notConfiguredBody")}
        />
      );
    }
    const policies = overview.policies ?? [];
    const deployments = overview.deployments ?? [];
    const blocklists = overview.blocklists ?? [];
    const filters = policies.flatMap((p) => p.contentFilters ?? []).filter((f) => f.enabled);
    return (
      <>
        <ResourceLine overview={overview} />
        <StatRow
          stats={[
            { label: t("stats.policies"), value: formatNumber(policies.length) },
            {
              label: t("stats.activeFilters"),
              value: formatNumber(filters.length),
              hint: t("stats.blocking", { count: filters.filter((f) => f.blocking).length }),
            },
            { label: t("stats.deployments"), value: formatNumber(deployments.length) },
            { label: t("stats.blocklists"), value: formatNumber(blocklists.length) },
          ]}
        />
        <div className="flex items-start gap-3 rounded-xl bg-info-subtle p-4" role="note">
          <Info size={18} className="mt-0.5 shrink-0 text-info-on-subtle" aria-hidden />
          <div className="flex flex-col gap-1">
            <Text variant="body-sm" className="text-info-on-subtle">
              {t("safety.title")}
            </Text>
            <Text variant="body-sm" tone="secondary">
              {t("safety.body")}
            </Text>
          </div>
        </div>
        <PageSection title={t("policies.title")}>
          {policies.length === 0 ? (
            <Muted text={t("policies.empty")} />
          ) : (
            <ul className="flex flex-col divide-y divide-border-subtle border-y border-border-subtle">
              {policies.map((policy) => (
                <PolicyItem key={policy.name} policy={policy} />
              ))}
            </ul>
          )}
        </PageSection>
        <PageSection title={t("deployments.title")}>
          <DeploymentTable deployments={deployments} policyNames={new Set(policies.map((p) => p.name))} />
        </PageSection>
        <PageSection title={t("blocklists.title")}>
          {blocklists.length === 0 ? <Muted text={t("blocklists.empty")} /> : <Blocklists blocklists={blocklists} />}
        </PageSection>
        <div className="flex flex-col gap-1 border-t border-border-subtle pt-4">
          <Muted text={t("footer.modify")} />
          <Muted text={t("footer.severity")} />
        </div>
      </>
    );
  };

  return (
    <AdminPage title={t("title")} subtitle={t("subtitle")}>
      {body()}
    </AdminPage>
  );
}
